import { createClient } from '@/lib/supabase/client'
import { EMP_QUALS, queryCurrentEmployee } from '@/lib/services/employees'
import { QUAL_FIELDS } from '#modules/qualifications'
import { timestampNow } from '#modules/timestamp'
import {
  cacheData,
  cacheCurrentEmployee,
  getPendingWrites,
  type TableName,
  markSynced,
  updateSyncMeta,
  getPendingCount,
} from './offlineStore'

// Tables whose inserts carry a client-generated id (client_request_id or a temp
// id), so a 23505 unique_violation on write means the row already landed from an
// earlier attempt — treat the duplicate as success rather than an error. Used by
// the offline flush below and the online direct-write path (useOfflineWrite).
// Typed as TableName[] so membership is checked against real Supabase tables.
export const idempotentTables: readonly TableName[] = [
  'dispense_admin_log',
  'patient_encounters',
  'supply_runs',
  'supply_run_items',
  'encounter_procedures'
]

let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
let syncInProgress = false
let listeners: ((online: boolean, pendingCount: number) => void)[] = []
let monitorInitialized = false

// ── Connection monitoring ─────────────────────────────────────────────────────

export function initConnectionMonitor() {
  if (typeof window === 'undefined') return
  if (monitorInitialized) return
  monitorInitialized = true

  window.addEventListener('online', () => {
    isOnline = true
    notifyListeners()
    flushPendingWrites()
    smartSync()
  })

  window.addEventListener('offline', () => {
    isOnline = false
    notifyListeners()
  })
}

export function onConnectionChange(fn: (online: boolean, pendingCount: number) => void) {
  listeners.push(fn)
  return () => {
    listeners = listeners.filter(l => l !== fn)
  }
}

async function notifyListeners() {
  const count = await getPendingCount()
  listeners.forEach(fn => fn(isOnline, count))
}

export function getIsOnline(): boolean {
  if (typeof navigator !== 'undefined') return navigator.onLine
  return isOnline
}

export async function getPendingWriteCount(): Promise<number> {
  return getPendingCount()
}

// ── Sync-complete events ─────────────────────────────────────────────────────

export function isSyncInProgress(): boolean {
  return dataSyncInProgress
}

type SyncStartListener = () => void
let syncStartListeners: SyncStartListener[] = []

export function onSyncStart(cb: SyncStartListener): () => void {
  syncStartListeners.push(cb)
  return () => { syncStartListeners = syncStartListeners.filter(l => l !== cb) }
}

function notifySyncStart() {
  syncStartListeners.forEach(cb => cb())
}

export type SyncCompleteEvent = { success: boolean; error?: unknown }

type SyncCompleteListener = (event: SyncCompleteEvent) => void
let syncCompleteListeners: SyncCompleteListener[] = []

export function onSyncComplete(cb: SyncCompleteListener): () => void {
  syncCompleteListeners.push(cb)
  return () => { syncCompleteListeners = syncCompleteListeners.filter(l => l !== cb) }
}

function notifySyncComplete(success: boolean, error?: unknown) {
  syncCompleteListeners.forEach(cb => cb({ success, error }))
}

let dataSyncInProgress = false

const SYNC_WINDOW_MS = 24 * 60 * 60 * 1000  // lookback window for operational data


// ── Helper: sync window cutoff (SYNC_WINDOW_MS) ────────────────────────────────

function syncWindowCutoff(): string {
  return new Date(Date.now() - SYNC_WINDOW_MS).toISOString()
}

// ── Helper: paginate large Supabase queries ───────────────────────────────────

const paginate = async (fetch: (from: number, to: number) => any): Promise<any[]> => {
  const rows: any[] = []
  for (let from = 0; ; from += 1000) {
    const { data } = await fetch(from, from + 999)
    if (!data || data.length === 0) break
    rows.push(...data)
    if (data.length < 1000) break
  }
  return rows
}

// -- Reference data sync: runs on every login --

async function syncColdData(): Promise<void> {
  const supabase = createClient()

  try {
    console.log('[Sync] Reference data sync...')

    const [[incidents, units, employees, incidentUnits, quals, currentEmp], formularyRows, catalogRows] = await Promise.all([
      Promise.all([
        supabase.from('incidents').select('*, incident_units(id, released_at)'),
        supabase.from('units').select(`*, unit_type:unit_types(name, sort_order, deploys_on_fireline), incident_units(id, incident_id, released_at, incident:incidents(name, status), unit_assignments(id, released_at, employee:employees(id, name, ${EMP_QUALS})))`),
        supabase.from('employees').select(`*, ${EMP_QUALS}`),
        supabase.from('incident_units').select('id, incident_id, released_at, unit:units(id, name, unit_type:unit_types(name))'),
        supabase.from('qualifications').select(QUAL_FIELDS).order('sort_order'),
        queryCurrentEmployee(),
      ]),
      paginate((from, to) => supabase.from('formulary_templates').select('*, catalog_item:item_catalog(category, sku, is_als)').order('id').range(from, to)),
      paginate((from, to) => supabase.from('item_catalog').select('*').order('id').range(from, to)),
    ])
    const formulary = { data: formularyRows }

    console.log('[Sync] Ref data:', {
      incidents: incidents.data?.length,
      units: units.data?.length,
      employees: employees.data?.length,
      formulary: formulary.data?.length,
      catalog: catalogRows.length,
      incidentUnits: incidentUnits.data?.length,
      qualifications: quals.data?.length,
    })

    if (incidents.data?.length) await cacheData('incidents', incidents.data, true)
    if (units.data?.length) await cacheData('units', units.data, true)
    if (employees.data?.length) await cacheData('employees', employees.data, true)
    if (formulary.data?.length) await cacheData('formulary', formulary.data, true)
    if (catalogRows.length) await cacheData('item_catalog', catalogRows, true)
    if (incidentUnits.data?.length) await cacheData('incident_units', incidentUnits.data, true)
    if (quals.data?.length) await cacheData('qualifications', quals.data, true)

    // Cache current user's assignment alongside their employee record, resolved from
    // the already-fetched units data. Stored separately so page-level unit queries can't corrupt it.
    if (currentEmp.data && units.data) {
      let unit = null, incidentUnit = null, incident = null
      outer: for (const u of units.data as any[]) {
        for (const iu of (u.incident_units ?? [])) {
          const mine = (iu.unit_assignments ?? []).find(
            (a: any) => a.employee?.id === currentEmp.data!.id && !a.released_at
          )
          if (mine && !iu.released_at && iu.incident?.status === 'Active') {
            unit = { id: u.id, name: u.name }
            incidentUnit = { id: iu.id, unit_id: u.id, incident_id: iu.incident_id }
            incident = iu.incident ? { id: iu.incident_id, name: iu.incident.name } : null
            break outer
          }
        }
      }
      await cacheCurrentEmployee(currentEmp.data, unit, incidentUnit, incident)
    }

    console.log('[Sync] Reference data sync complete')
  } catch (err) {
    console.error('[Sync] Reference data sync failed:', err)
    throw err
  }
}

// -- Operational data sync: time-windowed, runs on every login --

async function syncHotData(): Promise<void> {

  const supabase = createClient()

  try {
    const since = syncWindowCutoff()
    console.log('[Sync] Operational data sync (last', SYNC_WINDOW_MS / 3600000, 'h)...')

    // Patient data — last 48 hours
    const [encounters, mar, vitals, progressNotes, procedures] = await Promise.all([
      supabase.from('patient_encounters').select('*')
        .is('deleted_at', null)
        .gte('created_at', since)
        .order('created_at', { ascending: false }),
      supabase.from('dispense_admin_log').select('*')
        .gte('created_at', since)
        .order('created_at', { ascending: false }),
      supabase.from('encounter_vitals').select('*')
        .gte('recorded_at', since)
        .order('recorded_at', { ascending: false }),
      supabase.from('progress_notes').select('*')
        .is('deleted_at', null)
        .gte('note_datetime', since)
        .order('note_datetime', { ascending: false }),
      supabase.from('encounter_procedures').select('*')
        .gte('performed_at', since)
        .order('performed_at', { ascending: false }),
    ])

    console.log('[Sync] Ops (patient):', {
      encounters: encounters.data?.length,
      mar: mar.data?.length,
      vitals: vitals.data?.length,
      progressNotes: progressNotes.data?.length,
      procedures: procedures.data?.length,
    })

    if (encounters.data) await cacheData('encounters', encounters.data)
    if (mar.data) await cacheData('mar_entries', mar.data)
    if (vitals.data) await cacheData('vitals', vitals.data)
    if (progressNotes.data) {
      try { await cacheData('progress_notes' as any, progressNotes.data) } catch {}
    }
    if (procedures.data) {
      try { await cacheData('procedures' as any, procedures.data) } catch {}
    }

    // Operations data — supply runs (48h) + current inventory snapshot
    const [supplyRuns, supplyRunItems, invRows] = await Promise.all([
      supabase.from('supply_runs').select('*, incident:incidents(name), supply_run_items(*)')
        .gte('created_at', since)
        .order('created_at', { ascending: false }),
      supabase.from('supply_run_items').select('*')
        .gte('created_at', since)
        .order('id', { ascending: false }),
      paginate((from, to) => supabase.from('unit_inventory')
        .select('id, item_name, category, quantity, par_qty, lot_number, expiration_date, unit_id, incident_unit_id, barcode, upc, catalog_item_id')
        .order('id').range(from, to)),
    ])

    console.log('[Sync] Ops (supply/inv):', {
      supplyRuns: supplyRuns.data?.length,
      supplyRunItems: supplyRunItems.data?.length,
      inventory: invRows.length,
    })

    if (supplyRuns.data) await cacheData('supply_runs', supplyRuns.data)
    if (supplyRunItems.data) await cacheData('supply_run_items', supplyRunItems.data)
    if (invRows.length) await cacheData('inventory', invRows)

    await updateSyncMeta(timestampNow())
    console.log('[Sync] Operational data sync complete')
  } catch (err) {
    console.error('[Sync] Operational data sync failed:', err)
    throw err
  }
}

// -- Sync entry point --
// options.full (default true): run reference data sync + operational sync.
// options.full false: operational sync only (background refresh, no loading screen).
// Full sync deduplicates by waiting for any in-progress sync to finish.
// Background sync skips if a sync is already running (fire-and-forget).

export async function smartSync(options: { full?: boolean } = {}): Promise<SyncCompleteEvent> {
  const full = options.full !== false
  if (!getIsOnline()) return { success: false }
  if (dataSyncInProgress) {
    if (!full) return { success: false }  // background: skip, don't queue
    return new Promise<SyncCompleteEvent>(resolve => {
      const unsub = onSyncComplete(event => { unsub(); resolve(event) })
    })
  }
  dataSyncInProgress = true
  notifySyncStart()
  try {
    if (full) await syncColdData()
    await syncHotData()
    notifySyncComplete(true)
    return { success: true }
  } catch (err) {
    notifySyncComplete(false, err)
    return { success: false, error: err }
  } finally {
    dataSyncInProgress = false
  }
}

// ── Flush pending offline writes to Supabase ──────────────────────────────────

export async function flushPendingWrites(): Promise<void> {
  if (!getIsOnline() || syncInProgress) return
  syncInProgress = true

  const permanentErrorCodes = [
    '42703', // column does not exist
    '42P01', // table does not exist
    '23502', // NOT NULL violation
    '22P02', // invalid input syntax
    '42501', // insufficient privilege (RLS)
  ]

  try {
    const supabase = createClient()
    const pending = await getPendingWrites()

    if (pending.length === 0) return

    console.log('[Sync] Flushing', pending.length, 'pending writes...')

    pending.sort((a, b) => {
      const cmp = a.timestamp.localeCompare(b.timestamp)
      if (cmp !== 0) return cmp
      return (a.id as number) - (b.id as number)
    })

    for (const item of pending) {
      try {
        console.log('[Sync] Processing pending write:', {
          id: item.id,
          table: item.table,
          operation: item.operation,
          timestamp: item.timestamp,
          data: item.data,
        })

        // supabase.from() per-table overloads can't resolve a runtime table
        // name; payloads are typed at enqueue (see queueOfflineWrite).
        const q = supabase.from(item.table) as any

        if (item.operation === 'insert') {
          const { error } = await q.insert(item.data)
          if (error) {
            if (error.code === '23505' && idempotentTables.includes(item.table)) {
              console.warn(`[Sync] Duplicate ${item.table} — already exists, marking synced:`, item.id)
              await markSynced(item.id)
              continue
            }
            throw error
          }
        } else if (item.operation === 'update') {
          const { id, ...rest } = item.data
          const { error } = await q.update(rest).eq('id', id)
          if (error) throw error
        } else if (item.operation === 'delete') {
          const { error } = await q.delete().eq('id', item.data.id)
          if (error) throw error
        }

        console.log(`[Sync] ✓ Synced ${item.table} ${item.operation} queue-id=${item.id}`)
        await markSynced(item.id)
      } catch (err: any) {
        const errCode: string = err?.code || ''
        const msg: string = err?.message || String(err)
        console.error('[Sync] Pending write failed:', {
          queueId: item.id,
          table: item.table,
          operation: item.operation,
          timestamp: item.timestamp,
          message: msg,
          code: errCode,
          details: err?.details,
          hint: err?.hint,
          payload: item.data,
        })
        const isPermanent = permanentErrorCodes.some(code => msg.includes(code) || errCode === code)
        if (isPermanent) {
          console.warn('[Sync] Permanent error — removing from queue:', item.id, errCode, msg)
          await markSynced(item.id)
        } else {
          console.warn('[Sync] Transient error — will retry next flush:', item.id)
        }
      }
    }
  } finally {
    syncInProgress = false
    await notifyListeners()
  }
}
